import CloseIcon from '@mui/icons-material/Close'
import { Alert, Box, Dialog, DialogContent, IconButton, Tab, Tabs, Typography, useMediaQuery } from '@mui/material'
import React from 'react'
import { useTranslations } from 'use-intl'
import ForgotPasswordForm from './ForgotPasswordForm'
import SignInForm from './SignInForm'
import SignUpForm from './SignUpForm'

interface GoogleUserData {
    email: string
    firstName: string
    lastName: string
    fullName: string
    googleId: string
    picture?: string
    userId?: string | number
}

export interface SignInModalProps {
    open: boolean
    onClose: () => void
    form: any
    status: any
    handlers: any
    benefits: { text: string; icon: React.ReactNode }[]
    t: (key: string) => string
    onGoogleSignIn?: (userData: GoogleUserData) => void
}

export const SignInModal: React.FC<SignInModalProps> = ({
    open,
    onClose,
    form,
    status,
    handlers,
    benefits,
    onGoogleSignIn,
    t
}) => {
    const tSignInModal = useTranslations('signInModal')
    const tHeader = useTranslations('header')
    const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'))

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 99 }}>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent sx={{ display: 'flex', p: 0, minHeight: 500 }}>
                {/* Left: Benefits */}
                {!isMobile && (
                    <Box
                        sx={{
                            bgcolor: '#194E91',
                            color: 'common.white',
                            flex: 1,
                            p: 5,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            minWidth: 350
                        }}
                    >
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            {t('registerWithFlyin')}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ mb: 4, fontWeight: 400 }}>
                            {t('getFollowingBenefits')}
                        </Typography>
                        <Box component="ul" sx={{ pl: 0, m: 0, listStyle: 'none' }}>
                            {benefits.map((b, i) => (
                                <Box
                                    component="li"
                                    key={i}
                                    sx={{ display: 'flex', alignItems: 'center', mb: 4, fontSize: 16 }}
                                >
                                    <Box sx={{ mr: 3, display: 'flex', alignItems: 'center', color: 'common.white' }}>
                                        {b.icon}
                                    </Box>
                                    <Typography sx={{ fontSize: 14, fontWeight: 400, color: 'common.white' }}>
                                        {tSignInModal(b.text) !== `signInModal.${b.text}`
                                            ? tSignInModal(b.text)
                                            : tHeader(b.text)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
                {/* Right: Form */}
                <Box
                    sx={{
                        flex: 1.2,
                        p: isMobile ? 3 : 5,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        minWidth: 300
                    }}
                >
                    {!status.forgot ? (
                        <>
                            <Tabs value={status.tab} onChange={handlers.onTabChange} sx={{ mb: 3, mx: 2 }}>
                                <Tab label={t('signIn')} sx={{ fontSize: isMobile ? 14 : 18 }} />
                                <Tab label={t('signUp')} sx={{ fontSize: isMobile ? 14 : 18 }} />
                            </Tabs>
                            {status.error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {status.error}
                                </Alert>
                            )}
                            {status.success && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    {status.success}
                                </Alert>
                            )}
                            {status.tab === 0 ? (
                                <SignInForm
                                    isMobile={isMobile}
                                    form={form}
                                    status={status}
                                    handlers={handlers}
                                    t={t}
                                    tHeader={tHeader}
                                    onGoogleSignIn={onGoogleSignIn || (() => {})}
                                />
                            ) : (
                                <SignUpForm
                                    form={form}
                                    status={status}
                                    handlers={handlers}
                                    t={t}
                                    tHeader={tHeader}
                                    onGoogleSignIn={onGoogleSignIn || (() => {})}
                                />
                            )}
                        </>
                    ) : (
                        <ForgotPasswordForm form={form} status={status} handlers={handlers} t={t} />
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    )
}
